import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { addUser } from "../utils/usersApi";
import { logAction } from "../utils/logsApi";
import Modal from "./Modal";
import "../App.css";

const AddUser = () => {
    const [userData, setUserData] = useState({
        rawUserId: "",
        mail: "",
        fullName: "",
        product: "AUTOLABS",
        licensekey: "",
    });

    const [modal, setModal] = useState({
        isOpen: false,
        title: "",
        message: "",
        type: "info",
        onConfirm: null,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const userRole = Number(localStorage.getItem("userRole"));

    // Authentication check
    useEffect(() => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            navigate("/");
            return;
        }
    }, [navigate]);

    function sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms * 1000));
    }

    const openModal = (title, message, type = "info", onConfirm = null) => {
        setModal({ isOpen: true, title, message, type, onConfirm });
    };

    const closeModal = () => {
        setModal({ isOpen: false, title: "", message: "", type: "info", onConfirm: null });
        navigate('/')
    };

    const handleChange = (field, value) => {
        setUserData((prev) => ({ ...prev, [field]: value }));
    };

    const validateInput = () => {
        if (userData.rawUserId.length < 6) {
            return "User ID must be at least 6 digits.";
        }
        if (!userData.mail || !/\S+@\S+\.\S+/.test(userData.mail)) {
            return "Please enter a valid email.";
        }
        if (!userData.fullName.trim()) {
            return "Full Name is required.";
        }
        if (!["AUTOLABS", "STAFF"].includes(userData.product)) {
            return "Invalid product selected.";
        }
        if (userData.product === "STAFF" && userRole < 2) {
            return "You need role 2 or higher to add STAFF users.";
        }
        if (userData.licenseDuration && (userData.licenseDuration < 30 || userData.licenseDuration > 365) && userRole < 2) {
            return "License duration must be between 30 and 365 days.";
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validateInput();
        if (validationError) {
            setError(validationError);
            return;
        }


        setLoading(true);
        setError("");

        const payload = {
            rawUserId: userData.rawUserId,
            mail: userData.mail,
            fullName: userData.fullName,
            birthDate: "2000-01-01",
            product: userData.product,
            licensekey: userData.licensekey || undefined,
            expirationDays: userData.licenseDuration || (userData.product === "AUTOLABS" ? 180 : 365)
        };

        try {
            const response = await addUser(payload);

            if (response.success) {
                await sleep(5)
                openModal("Success", `User ${payload.fullName} added successfully!`, "info");
                logAction(
                    "ADD_USER",
                    localStorage.getItem("userEmail"),
                    `User Added: ${payload.fullName}`,
                    payload.userId
                );
            } else {
                openModal("Error", `Error deleting user: ${response.message}`, "info");
            }
        } catch (error) {
            console.error("Error handling user deletion:", error);
            openModal("Error", "An error occurred while deleting the user.", "info");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-user-container">
            <h2>Add User</h2>
            <form onSubmit={handleSubmit}>
                <div>
                    <label>Raw User ID</label>
                    <input
                        type="text"
                        value={userData.rawUserId}
                        onChange={(e) => handleChange("rawUserId", e.target.value)}
                        placeholder="Enter at least 6 digits"
                    />
                </div>
                <div>
                    <label>Email</label>
                    <input
                        type="email"
                        value={userData.mail}
                        onChange={(e) => handleChange("mail", e.target.value)}
                        placeholder="example@domain.com"
                    />
                </div>
                <div>
                    <label>Full Name</label>
                    <input
                        type="text"
                        value={userData.fullName}
                        onChange={(e) => handleChange("fullName", e.target.value)}
                        placeholder="Enter full name"
                    />
                </div>
                <div>
                    <label>License Duration (Days)</label>
                    <input
                        type="number"
                        value={userData.licenseDuration}
                        onChange={(e) => handleChange("licenseDuration", Number(e.target.value))}
                        placeholder="Leave blank for default"
                        min={userRole >= 2 ? undefined : 30}
                        max={userRole >= 2 ? undefined : 365}
                    />
                </div>
                <div>
                    <label>Product</label>
                    <select
                        value={userData.product}
                        onChange={(e) => handleChange("product", e.target.value)}
                        disabled={userRole < 2}
                    >
                        <option value="AUTOLABS">AUTOLABS</option>
                        <option value="STAFF" disabled={userRole < 2}>
                            STAFF {userRole < 2 ? "(Role 2+ Required)" : ""}
                        </option>
                    </select>

                </div>
                {error && <p style={{ color: "red" }}>{error}</p>}
                <button type="submit" disabled={loading}>
                    {loading ? "Adding..." : "Add User"}
                </button>
            </form>
            <Modal
                isOpen={modal.isOpen}
                title={modal.title}
                message={modal.message}
                type={modal.type}
                onClose={closeModal}
                onConfirm={modal.onConfirm}
            />
        </div>
    );
};

export default AddUser;
